import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Building2, Plus, Edit, Trash2, Users, UserCheck, UserPlus } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { LogoUpload } from "@/components/empresa/LogoUpload";
import { getImageUrl } from "@/lib/imageUtils";

interface Empresa {
  id: string;
  nombre: string;
  razon_social: string | null;
  nif_cif: string | null;
  estado: string;
  plan_suscripcion: string;
  limite_usuarios: number;
  limite_clientes: number;
  created_at: string;
  usuario_count?: number;
  cliente_count?: number;
  logo_url?: string | null;
}

export default function GestionEmpresas() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState<Empresa | null>(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedEmpresaForUser, setSelectedEmpresaForUser] = useState<Empresa | null>(null);
  const [userData, setUserData] = useState({
    nombre: "",
    email: "",
    password: "",
    rol: "admin"
  });
  const [formData, setFormData] = useState({
    nombre: "",
    razon_social: "",
    nif_cif: "",
    estado: "activa",
    plan_suscripcion: "basico",
    limite_usuarios: 5,
    limite_clientes: 50,
  });

  useEffect(() => {
    console.log('[GestionEmpresas] hasRole check:', hasRole('super_admin'));
    if (!hasRole('super_admin')) {
      console.log('[GestionEmpresas] No super_admin role, redirecting to /');
      navigate('/');
      return;
    }
    console.log('[GestionEmpresas] Fetching empresas...');
    fetchEmpresas();
  }, [hasRole, navigate]);

  const fetchEmpresas = async () => {
    try {
      setIsLoading(true);
      console.log('[GestionEmpresas] Starting query to empresas table...');
      const { data, error } = await supabase
        .from('empresas')
        .select(`
          *,
          perfiles:perfiles(count),
          clientes:clientes(count)
        `)
        .order('created_at', { ascending: false });

      console.log('[GestionEmpresas] Query result:', { data, error });
      if (error) throw error;

      const empresasWithCounts = data?.map(empresa => ({
        ...empresa,
        usuario_count: empresa.perfiles?.[0]?.count || 0,
        cliente_count: empresa.clientes?.[0]?.count || 0,
      })) || [];

      console.log('[GestionEmpresas] Empresas with counts:', empresasWithCounts);
      setEmpresas(empresasWithCounts);
    } catch (error: any) {
      console.error('[GestionEmpresas] Error fetching empresas:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las empresas",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingEmpresa) {
        const { error } = await supabase
          .from('empresas')
          .update(formData)
          .eq('id', editingEmpresa.id);

        if (error) throw error;

        toast({
          title: "Empresa actualizada",
          description: "La empresa se ha actualizado correctamente",
        });
      } else {
        const { error } = await supabase
          .from('empresas')
          .insert([formData]);

        if (error) throw error;

        toast({
          title: "Empresa creada",
          description: "La empresa se ha creado correctamente",
        });
      }

      setDialogOpen(false);
      setEditingEmpresa(null);
      setFormData({
        nombre: "",
        razon_social: "",
        nif_cif: "",
        estado: "activa",
        plan_suscripcion: "basico",
        limite_usuarios: 5,
        limite_clientes: 50,
      });
      fetchEmpresas();
    } catch (error: any) {
      console.error('Error saving empresa:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar la empresa",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (empresaId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta empresa? Esto eliminará todos sus datos asociados.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('empresas')
        .delete()
        .eq('id', empresaId);

      if (error) throw error;

      toast({
        title: "Empresa eliminada",
        description: "La empresa y todos sus datos han sido eliminados",
      });
      fetchEmpresas();
    } catch (error: any) {
      console.error('Error deleting empresa:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la empresa",
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (empresa: Empresa) => {
    setEditingEmpresa(empresa);
    setFormData({
      nombre: empresa.nombre,
      razon_social: empresa.razon_social || "",
      nif_cif: empresa.nif_cif || "",
      estado: empresa.estado,
      plan_suscripcion: empresa.plan_suscripcion,
      limite_usuarios: empresa.limite_usuarios,
      limite_clientes: empresa.limite_clientes,
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingEmpresa(null);
    setFormData({
      nombre: "",
      razon_social: "",
      nif_cif: "",
      estado: "activa",
      plan_suscripcion: "basico",
      limite_usuarios: 5,
      limite_clientes: 50,
    });
    setDialogOpen(true);
  };

  const openUserDialog = (empresa: Empresa) => {
    setSelectedEmpresaForUser(empresa);
    setUserData({
      nombre: "",
      email: "",
      password: "",
      rol: "admin"
    });
    setUserDialogOpen(true);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedEmpresaForUser || !userData.nombre || !userData.email || !userData.password) {
      toast({
        title: "Error",
        description: "Todos los campos son obligatorios",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: result, error: invokeError } = await supabase.functions.invoke('create-user', {
        body: {
          email: userData.email,
          password: userData.password,
          nombre: userData.nombre,
          rol: userData.rol,
          empresa_id: selectedEmpresaForUser.id,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message || "Error al crear el usuario");
      }
      if (result?.error) {
        throw new Error(result.error);
      }

      toast({
        title: "Usuario creado",
        description: `Usuario ${userData.rol} creado correctamente para ${selectedEmpresaForUser.nombre}`,
      });

      setUserDialogOpen(false);
      setUserData({
        nombre: "",
        email: "",
        password: "",
        rol: "admin"
      });
      fetchEmpresas();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo crear el usuario",
        variant: "destructive",
      });
    }
  };

  const getEstadoBadgeVariant = (estado: string) => {
    switch (estado) {
      case 'activa': return 'default';
      case 'suspendida': return 'secondary';
      case 'cancelada': return 'destructive';
      default: return 'outline';
    }
  };

  const getPlanBadgeVariant = (plan: string) => {
    switch (plan) {
      case 'empresarial': return 'default';
      case 'profesional': return 'secondary';
      case 'basico': return 'outline';
      default: return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gestión de Empresas</h1>
          <p className="text-muted-foreground mt-1">
            Administra las empresas y sus suscripciones
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva Empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>
                  {editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
                </DialogTitle>
                <DialogDescription>
                  {editingEmpresa 
                    ? 'Actualiza los datos de la empresa' 
                    : 'Crea una nueva empresa en el sistema'}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="razon_social">Razón Social</Label>
                  <Input
                    id="razon_social"
                    value={formData.razon_social}
                    onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="nif_cif">NIF/CIF</Label>
                  <Input
                    id="nif_cif"
                    value={formData.nif_cif}
                    onChange={(e) => setFormData({ ...formData, nif_cif: e.target.value })}
                  />
                </div>
                
                {editingEmpresa && (
                  <LogoUpload
                    empresaId={editingEmpresa.id}
                    currentLogoUrl={getImageUrl(editingEmpresa.logo_url)}
                    onLogoUpdated={(newUrl) => {
                      setEditingEmpresa({
                        ...editingEmpresa,
                        logo_url: newUrl
                      });
                      fetchEmpresas();
                    }}
                  />
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="estado">Estado</Label>
                    <Select 
                      value={formData.estado} 
                      onValueChange={(value) => setFormData({ ...formData, estado: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="activa">Activa</SelectItem>
                        <SelectItem value="suspendida">Suspendida</SelectItem>
                        <SelectItem value="cancelada">Cancelada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="plan">Plan</Label>
                    <Select 
                      value={formData.plan_suscripcion} 
                      onValueChange={(value) => setFormData({ ...formData, plan_suscripcion: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basico">Básico</SelectItem>
                        <SelectItem value="profesional">Profesional</SelectItem>
                        <SelectItem value="empresarial">Empresarial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="limite_usuarios">Límite Usuarios</Label>
                    <Input
                      id="limite_usuarios"
                      type="number"
                      min="1"
                      value={formData.limite_usuarios}
                      onChange={(e) => setFormData({ ...formData, limite_usuarios: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="limite_clientes">Límite Clientes</Label>
                    <Input
                      id="limite_clientes"
                      type="number"
                      min="1"
                      value={formData.limite_clientes}
                      onChange={(e) => setFormData({ ...formData, limite_clientes: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit">
                  {editingEmpresa ? 'Actualizar' : 'Crear'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <form onSubmit={handleCreateUser}>
              <DialogHeader>
                <DialogTitle>Crear Usuario</DialogTitle>
                <DialogDescription>
                  Crear nuevo usuario para {selectedEmpresaForUser?.nombre}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="user_nombre">Nombre *</Label>
                  <Input
                    id="user_nombre"
                    value={userData.nombre}
                    onChange={(e) => setUserData({ ...userData, nombre: e.target.value })}
                    placeholder="Nombre completo"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="user_email">Email *</Label>
                  <Input
                    id="user_email"
                    type="email"
                    value={userData.email}
                    onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                    placeholder="usuario@email.com"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="user_password">Contraseña *</Label>
                  <Input
                    id="user_password"
                    type="password"
                    value={userData.password}
                    onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="user_rol">Rol</Label>
                  <Select 
                    value={userData.rol} 
                    onValueChange={(value) => setUserData({ ...userData, rol: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="gestor">Gestor</SelectItem>
                      <SelectItem value="tecnico">Técnico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit">Crear Usuario</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {empresas.map((empresa) => (
          <Card key={empresa.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {empresa.logo_url ? (
                    <img
                      src={getImageUrl(empresa.logo_url) || ""}
                      alt={empresa.nombre}
                      className="h-10 w-10 object-contain rounded-md border border-border"
                    />
                  ) : (
                    <Building2 className="h-5 w-5" />
                  )}
                  <CardTitle className="text-lg">{empresa.nombre}</CardTitle>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openUserDialog(empresa)}
                    title="Crear usuario"
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(empresa)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(empresa.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardDescription>
                {empresa.razon_social || empresa.nif_cif || 'Sin información fiscal'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Badge variant={getEstadoBadgeVariant(empresa.estado)}>
                  {empresa.estado.charAt(0).toUpperCase() + empresa.estado.slice(1)}
                </Badge>
                <Badge variant={getPlanBadgeVariant(empresa.plan_suscripcion)}>
                  {empresa.plan_suscripcion.charAt(0).toUpperCase() + empresa.plan_suscripcion.slice(1)}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>Usuarios</span>
                  </div>
                  <span className="font-medium">
                    {empresa.usuario_count || 0} / {empresa.limite_usuarios}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <UserCheck className="h-4 w-4" />
                    <span>Clientes</span>
                  </div>
                  <span className="font-medium">
                    {empresa.cliente_count || 0} / {empresa.limite_clientes}
                  </span>
                </div>
              </div>
              <div className="pt-2 border-t text-xs text-muted-foreground">
                Creada: {new Date(empresa.created_at).toLocaleDateString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {empresas.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No hay empresas</h3>
            <p className="text-muted-foreground mb-4">
              Crea tu primera empresa para comenzar
            </p>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Crear Primera Empresa
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}